import React, { useState, useEffect, useContext } from 'react';
import './ListWrapper.scss';
import PullToRefresh from '../PullToRefresh';
import LoadMore from '../LoadMore';
import intl from 'react-intl-universal';
import { PageContext } from '../ComponentNavigatorV2/PageContext';

interface IListWrapper {
  onRefresh: (resolve?: any) => PromiseLike<any>;
  isContainer?: boolean;
  children: React.ReactNode;
  // 需要返回一个boolean标识当前是否已经加载到底了
  onLoadMore: (resolve?: any) => PromiseLike<ILoadMoreResult>;
  disable?: boolean;
  scrollerChildrenSelector: string;
  eventName: string; // 注意，请自行保证eventName的唯一性，否则对于监听同一个eventName的会同时响应
  total?: number; // 总条数，如果总条数为0的话，不需要显示bottomText，因为有其他处理了
}

export interface ILoadMoreResult {
  nomore: boolean;
  message: string;
}

const BOTTOM_LOADING_DEFAULT = true;

const ListWrapper = function (props: IListWrapper) {
  const BOTTOM_TEXT_DEFAULT = intl.get('Loading');
  // 默认状态用 到底，主要是第一次加载的时候 没有调用refresh，如果又没有状态，会让用户直接看到这个文字
  const [bottomLoading, setBottomLoading] = useState(false);
  const [bottomText, setBottomText] = useState(
    Number(props!.total) <= 0
      ? ''
      : intl.get('This is the end', { words: 'photos' })
  );

  const [isEnd, setIsEnd] = useState(false); // 是否到底了

  // const theEventName = `to-bottom-${props.eventName}`;
  const context = useContext(PageContext);

  useEffect(() => {
    const theEventName = `page-scroll-bottom-${context.cid}`;

    const toBottomListener = function () {
      // console.log('tobottom');
      // 只检查是否已经到底，移除isLoadingMore检查避免状态竞争
      if (isEnd) {
        // console.log('已到底，跳过本次请求');
        return;
      }

      setBottomLoading(true);
      setBottomText(BOTTOM_TEXT_DEFAULT);

      // 检查是否还在底部的函数
      const checkAndLoadMoreIfAtBottom = () => {
        // 获取滚动容器
        const scrollContainer = document.querySelector(props.scrollerChildrenSelector || '.gallery-home') as HTMLElement;
        if (scrollContainer) {
          const { offsetHeight, scrollTop, scrollHeight } = scrollContainer;
          const bottomDistance = 800; // 使用和useScroll相同的限制距离

          // 如果仍然在底部范围内，自动触发下一页加载
          if ((offsetHeight + scrollTop + bottomDistance) >= scrollHeight) {
            // console.log('数据加载完成后仍在底部，自动触发下一页');
            // 延时一下再次触发，避免立即触发导致的问题
            setTimeout(() => {
              // 多重检查确保安全：检查是否到底、是否还在底部、避免组件已卸载的情况
              if (!isEnd && scrollContainer.parentElement) {
                const currentState = scrollContainer;
                const { offsetHeight: currentHeight, scrollTop: currentScrollTop, scrollHeight: currentScrollHeight } = currentState;
                // 再次确认仍在底部才触发
                if ((currentHeight + currentScrollTop + bottomDistance) >= currentScrollHeight) {
                  toBottomListener();
                }
              }
            }, 100);
          }
        }
      };
      const loadPromise = props.onLoadMore();
      if (loadPromise) {
        loadPromise.then((result: ILoadMoreResult) => {
          if (result.nomore) {
            setIsEnd(true);
            setBottomText(result.message);
            setBottomLoading(false);
          } else {
            setIsEnd(false);
            setBottomText('');
            setBottomLoading(false);
            // 数据加载完成且还有更多数据时，检查是否需要继续加载
            checkAndLoadMoreIfAtBottom();
          }
        });
      } else {
        setBottomLoading(false);
      }
    };
    // console.log(`scroll 监听事件`, theEventName);
    if (!isEnd) {
      window.addEventListener(theEventName, toBottomListener, false);
    } else {
      window.removeEventListener(theEventName, toBottomListener);
    }
    return () => {
      console.log(`scroll 移动监听事件`, theEventName);
      window.removeEventListener(theEventName, toBottomListener);
    };
  }, [context.cid, isEnd]);

  // 监听修改isEnd的事件，尴尬，切换标签后，由于无法重置isEnd状态，会导致诸如热门一类的，一旦到底后，再切换到其他标签，再切换回热门，就无法加载下一页了，因为他当时已经是到底了
  useEffect(() => {
    const setIsEndFalse = 'set-is-end-false';
    const listener = function () {
      setIsEnd(false);
      setBottomText('');
      setBottomLoading(false);
    };
    window.addEventListener(setIsEndFalse, listener, false);
    return () => {
      window.removeEventListener(setIsEndFalse, listener);
    };
  }, []);

  const refresh = () => {
    // console.log('ListWrapper refresh');
    setIsEnd(false);
    setBottomLoading(BOTTOM_LOADING_DEFAULT);
    setBottomText(BOTTOM_TEXT_DEFAULT);

    if (props.onRefresh) {
      return props.onRefresh().then(dt => {
        if (!dt || !dt.length) {
          setIsEnd(true);
          setBottomText(intl.get('This is the end', { words: 'photos' }));
          setBottomLoading(false);
        }
      });
    } else {
      return new Promise<void>(resolve => resolve());
    }
  };

  return (
    <div className="list-wrapper">
      {props.isContainer ? (
        <>
          {props.children}
          <LoadMore loading={bottomLoading}>{bottomText}</LoadMore>
        </>
      ) : (
        <PullToRefresh
          onRefresh={refresh}
          disable={props.disable === true ? true : false}
          scrollerChildrenSelector={props.scrollerChildrenSelector}
        >
          {props.children}
          <LoadMore loading={bottomLoading}>{bottomText}</LoadMore>
        </PullToRefresh>
      )}
    </div>
  );
};
// 返回结果可以直接丢给load more 方法
export interface ICheckLoadMore {
  data: any[];
  total: number;
}
export const checkLoadMore = ({ data, total }: ICheckLoadMore) => {
  if (total === 0 || data.length === 0) {
    return {
      nomore: true,
      message: ''
    };
  } else if (data.length >= total) {
    return {
      nomore: true,
      message: intl.get('This is the end', { words: 'photos' })
    };
  }
  return {
    nomore: false,
    message: ''
  };
};

export default ListWrapper;
